/**
 * Milestone HUD — progress along the critical path from campaign start (or last
 * reached upstream milestone) to each milestone's gate.
 */

import { getEffectiveDependencyIds } from '../../../layout/graphBuilder';
import {
  isMilestoneNode,
  getMilestoneAnchor,
  getHostTask,
  isMilestoneReached,
  getNonMilestoneTasks,
} from '../../../utils/milestoneUtils';
import { isTaskCompleteForProgress, getRelevantTasksForProgress } from '../../../utils/progressUtils';
import {
  getAllConditionEntries,
  getDependencyTaskId,
  getDependencyBranch,
} from '../../../../shared/ConditionsDropdown/utils/conditionHelpers';

/**
 * Decision-aware graph context for a set of (non-milestone) tasks.
 * - `taskById`  fast lookup
 * - `excludedTaskIds` tasks that live entirely on a non-selected decision branch
 *   (computed with the same logic as campaign progress, incl. OR-path handling)
 */
function buildGraphContext(missionTasks) {
  const tasks = missionTasks || [];
  const taskById = new Map(tasks.map((t) => [t.id, t]));
  const { excludedTaskIds } = getRelevantTasksForProgress(tasks);
  return { missionTasks: tasks, taskById, excludedTaskIds };
}

/** Dependency entries {taskId, branch} for a task (v1/v2 conditions or legacy dependsOn). */
function getDependencyEntries(task, missionTasks) {
  const entries = getAllConditionEntries(task.unlockConditions, missionTasks);
  if (entries.length > 0) return entries;
  const deps = Array.isArray(task.dependsOn) ? task.dependsOn : [];
  return deps
    .map((dep) => ({ taskId: getDependencyTaskId(dep), branch: getDependencyBranch(dep) }))
    .filter((d) => d.taskId != null);
}

/**
 * Immediate upstream dependency IDs on the *selected* path only.
 * Drops:
 *   - deps that live entirely on a non-selected branch (excludedTaskIds), and
 *   - edges coming from a decided decision whose branch wasn't chosen.
 * Mirrors the exclusion logic used by campaign progress (progressUtils).
 */
function getSelectedDependencyIds(taskId, ctx) {
  const task = ctx.taskById.get(taskId);
  if (!task) return [];
  const entries = getDependencyEntries(task, ctx.missionTasks);
  const ids = [];
  for (const { taskId: depId, branch } of entries) {
    if (ctx.excludedTaskIds.has(depId)) continue;
    const parent = ctx.taskById.get(depId);
    const answer = parent?.answer;
    if (parent?.nodeType === 'decision' && answer && branch && branch !== answer) {
      continue;
    }
    ids.push(depId);
  }
  return ids;
}

/**
 * Collect ALL required upstream missions/decisions needed to complete `taskId`,
 * following only selected decision branches.
 *
 * This is the full transitive prerequisite closure (a union of every required
 * branch), NOT just the single longest chain — so parallel AND-prerequisites
 * (e.g. a short side-branch that must also be finished) are included rather than
 * being dropped in favour of the longest chain.
 *
 * Nested campaigns are represented by their single top-level parent node (we do
 * not descend into nested sub-missions), keeping the id set aligned with the
 * steps rendered in the path modal.
 */
function collectRequiredMissionIds(taskId, ctx, result, visiting) {
  // Already collected (post-order) → its whole dependency subtree is collected too.
  if (result.has(taskId) || visiting.has(taskId)) return;

  const task = ctx.taskById.get(taskId);
  if (!task || isMilestoneNode(task) || ctx.excludedTaskIds.has(taskId)) return;

  visiting.add(taskId);
  for (const depId of getSelectedDependencyIds(taskId, ctx)) {
    collectRequiredMissionIds(depId, ctx, result, visiting);
  }
  visiting.delete(taskId);

  result.add(taskId);
}

/** True when `taskId` transitively depends on `ancestorTaskId` in the mission graph. */
function hostTransitiveDependsOn(taskId, ancestorTaskId, missionTasks, memo = new Map(), visiting = new Set()) {
  if (taskId === ancestorTaskId) return false;
  const key = `${taskId}:${ancestorTaskId}`;
  if (memo.has(key)) return memo.get(key);
  if (visiting.has(taskId)) return false;

  const task = missionTasks.find((t) => t.id === taskId);
  if (!task) {
    memo.set(key, false);
    return false;
  }

  visiting.add(taskId);
  const deps = getEffectiveDependencyIds(task, missionTasks);

  let result = deps.includes(ancestorTaskId);
  if (!result) {
    result = deps.some((depId) => hostTransitiveDependsOn(depId, ancestorTaskId, missionTasks, memo, visiting));
  }

  visiting.delete(taskId);
  memo.set(key, result);
  return result;
}

/**
 * True when milestone A's gate must be passed before milestone B's gate.
 * Same host: input-side before output-side; same side ordered by task order.
 * Different hosts: B's host transitively depends on A's host.
 */
function milestoneMustPrecede(milestoneA, milestoneB, missionTasks) {
  if (milestoneA.id === milestoneB.id) return false;

  const anchorA = getMilestoneAnchor(milestoneA);
  const anchorB = getMilestoneAnchor(milestoneB);
  if (!anchorA || !anchorB) return false;

  const rankA = getMilestoneFlowRank(milestoneA, missionTasks);
  const rankB = getMilestoneFlowRank(milestoneB, missionTasks);
  if (rankA >= rankB) return false;

  if (anchorA.hostTaskId === anchorB.hostTaskId) {
    if (anchorA.side === 'input' && anchorB.side === 'output') return true;
    if (anchorA.side === anchorB.side) {
      return (milestoneA.order ?? 0) < (milestoneB.order ?? 0);
    }
    return false;
  }

  return hostTransitiveDependsOn(anchorB.hostTaskId, anchorA.hostTaskId, missionTasks);
}

function compareUnreachedMilestoneEntries(a, b) {
  if (a.progressPercent !== b.progressPercent) {
    return b.progressPercent - a.progressPercent;
  }
  if (a.flowRank !== b.flowRank) return a.flowRank - b.flowRank;
  const orderA = a.milestone.order ?? 0;
  const orderB = b.milestone.order ?? 0;
  if (orderA !== orderB) return orderA - orderB;
  return (a.milestone.name || '').localeCompare(b.milestone.name || '');
}

/**
 * Precedence-respecting order: among milestones with no unmet predecessors,
 * pick highest progress, then flow rank, then name. Reached milestones are excluded
 * from the HUD list (handled by celebration animation in MilestonesList).
 */
function sortMilestoneHudEntries(entries, missionTasks) {
  const unreached = entries.filter((e) => !e.reached);

  const byId = new Map(unreached.map((e) => [e.milestone.id, e]));
  const ids = [...byId.keys()];
  const inDegree = new Map(ids.map((id) => [id, 0]));
  const successors = new Map(ids.map((id) => [id, []]));

  for (let i = 0; i < ids.length; i += 1) {
    for (let j = 0; j < ids.length; j += 1) {
      if (i === j) continue;
      const a = byId.get(ids[i]).milestone;
      const b = byId.get(ids[j]).milestone;
      if (milestoneMustPrecede(a, b, missionTasks)) {
        successors.get(ids[i]).push(ids[j]);
        inDegree.set(ids[j], inDegree.get(ids[j]) + 1);
      }
    }
  }

  const sortedUnreached = [];
  const placed = new Set();

  while (sortedUnreached.length < unreached.length) {
    const ready = ids.filter((id) => !placed.has(id) && inDegree.get(id) === 0);
    if (ready.length === 0) {
      ids.filter((id) => !placed.has(id))
        .sort((idA, idB) => compareUnreachedMilestoneEntries(byId.get(idA), byId.get(idB)))
        .forEach((id) => {
          placed.add(id);
          sortedUnreached.push(byId.get(id));
        });
      break;
    }

    ready.sort((idA, idB) => compareUnreachedMilestoneEntries(byId.get(idA), byId.get(idB)));
    const pick = ready[0];
    placed.add(pick);
    sortedUnreached.push(byId.get(pick));

    for (const succ of successors.get(pick)) {
      inDegree.set(succ, inDegree.get(succ) - 1);
    }
  }

  return sortedUnreached;
}

/** Cap displayed progress so downstream cannot exceed blocked upstream milestones. */
function applyProgressCaps(entries, missionTasks) {
  const capped = entries.map((entry) => ({ ...entry }));

  let changed = true;
  while (changed) {
    changed = false;
    for (const entry of capped) {
      if (entry.reached) continue;
      for (const other of capped) {
        if (other.milestone.id === entry.milestone.id || other.reached) continue;
        if (milestoneMustPrecede(other.milestone, entry.milestone, missionTasks)) {
          const next = Math.min(entry.progressPercent, other.progressPercent);
          if (next < entry.progressPercent) {
            entry.progressPercent = next;
            changed = true;
          }
        }
      }
    }
  }

  return capped;
}

/** Flow order rank for sorting milestones (host depth, then input before output). */
export function getMilestoneFlowRank(milestone, missionTasks) {
  const anchor = getMilestoneAnchor(milestone);
  if (!anchor?.hostTaskId) return 0;

  const hostRankMemo = new Map();
  const hostRank = (taskId, visiting = new Set()) => {
    if (hostRankMemo.has(taskId)) return hostRankMemo.get(taskId);
    if (visiting.has(taskId)) return 0;
    visiting.add(taskId);
    const task = missionTasks.find((t) => t.id === taskId);
    if (!task) return 0;
    const deps = getEffectiveDependencyIds(task, missionTasks);
    let rank = 1;
    if (deps.length > 0) {
      rank = 1 + Math.max(...deps.map((d) => hostRank(d, visiting)));
    }
    hostRankMemo.set(taskId, rank);
    return rank;
  };

  const hr = hostRank(anchor.hostTaskId);
  return hr * 2 + (anchor.side === 'output' ? 1 : 0);
}

/**
 * Full set of missions/decisions required to reach this milestone's gate
 * (transitive prerequisite closure on selected decision branches).
 * - output-side: everything up to and including the host mission.
 * - input-side:  everything the host depends on (the host itself is not required
 *   for the gate, which fires as soon as the host becomes unlockable).
 */
export function getCriticalPathMissionIdsForMilestone(
  milestone,
  tasks,
  statuses,
  nestedSprints,
  allSprints,
) {
  const host = getHostTask(milestone, tasks);
  const anchor = getMilestoneAnchor(milestone);
  if (!host || !anchor) return new Set();

  const missionTasks = getNonMilestoneTasks(tasks);
  const ctx = buildGraphContext(missionTasks);
  const result = new Set();

  if (anchor.side === 'output') {
    collectRequiredMissionIds(host.id, ctx, result, new Set());
    return result;
  }

  const visiting = new Set();
  for (const depId of getSelectedDependencyIds(host.id, ctx)) {
    collectRequiredMissionIds(depId, ctx, result, visiting);
  }
  return result;
}

/** Flow depth rank for ordering missions on a timeline (deeper = later). */
export function getTaskFlowRank(taskId, missionTasks) {
  const memo = new Map();
  const rank = (id, visiting = new Set()) => {
    if (memo.has(id)) return memo.get(id);
    if (visiting.has(id)) return 0;
    visiting.add(id);
    const task = missionTasks.find((t) => t.id === id);
    if (!task) return 0;
    const deps = getEffectiveDependencyIds(task, missionTasks);
    let r = 1;
    if (deps.length > 0) {
      r = 1 + Math.max(...deps.map((d) => rank(d, visiting)));
    }
    memo.set(id, r);
    return r;
  };
  return rank(taskId);
}

/**
 * Mission IDs in the progress segment (from last reached upstream milestone to this gate).
 */
export function getMilestoneProgressSegmentIds(
  milestone,
  tasks,
  statuses,
  nestedSprints,
  allSprints,
  allMilestones,
) {
  const pathToMilestone = getCriticalPathMissionIdsForMilestone(
    milestone, tasks, statuses, nestedSprints, allSprints,
  );

  if (pathToMilestone.size === 0) {
    return { segmentIds: new Set(), baseline: null, pathToMilestone };
  }

  const missionTasks = getNonMilestoneTasks(tasks);
  const baseline = getBaselineReachedMilestone(
    milestone, allMilestones, missionTasks, tasks, statuses, nestedSprints, allSprints,
  );

  let segmentIds = pathToMilestone;
  if (baseline) {
    const pathToBaseline = getCriticalPathMissionIdsForMilestone(
      baseline, tasks, statuses, nestedSprints, allSprints,
    );
    segmentIds = new Set(
      [...pathToMilestone].filter((id) => !pathToBaseline.has(id)),
    );
    if (segmentIds.size === 0) {
      segmentIds = pathToMilestone;
    }
  }

  return { segmentIds, baseline, pathToMilestone };
}

/**
 * Ordered timeline steps required before a milestone is reached.
 * @returns {{ milestone, reached, progressPercent, baselineMilestone, steps }}
 */
export function getMilestonePathTimeline(
  milestone,
  tasks,
  statuses = [],
  nestedSprints = [],
  allSprints = [],
) {
  const allMilestones = (tasks || []).filter(isMilestoneNode);
  const missionTasks = getNonMilestoneTasks(tasks);
  const reached = isMilestoneReached(milestone, tasks, nestedSprints, statuses);
  const { segmentIds, baseline } = getMilestoneProgressSegmentIds(
    milestone, tasks, statuses, nestedSprints, allSprints, allMilestones,
  );

  const taskMap = new Map(missionTasks.map((t) => [t.id, t]));
  const steps = [...segmentIds]
    .map((id) => taskMap.get(id))
    .filter(Boolean)
    .sort((a, b) => getTaskFlowRank(a.id, missionTasks) - getTaskFlowRank(b.id, missionTasks))
    .map((task) => ({
      task,
      complete: isTaskCompleteForProgress(task, statuses),
      flowRank: getTaskFlowRank(task.id, missionTasks),
      nodeType: task.nodeType || 'task',
    }));

  // Derive the percentage from the exact steps shown so the "X of Y complete"
  // subtitle and the headline percentage can never disagree.
  const completeSteps = steps.filter((s) => s.complete).length;
  const progressPercent = reached
    ? 100
    : steps.length > 0
      ? Math.round((completeSteps / steps.length) * 100)
      : 0;

  return {
    milestone,
    reached,
    progressPercent,
    baselineMilestone: baseline,
    steps,
  };
}

function getBaselineReachedMilestone(milestone, milestones, missionTasks, tasks, statuses, nestedSprints, allSprints) {
  const myRank = getMilestoneFlowRank(milestone, missionTasks);
  const upstreamReached = milestones.filter(
    (m) => m.id !== milestone.id
      && getMilestoneFlowRank(m, missionTasks) < myRank
      && isMilestoneReached(m, tasks, nestedSprints, statuses),
  );
  if (upstreamReached.length === 0) return null;
  return upstreamReached.reduce((best, m) => (
    getMilestoneFlowRank(m, missionTasks) > getMilestoneFlowRank(best, missionTasks) ? m : best
  ));
}

/**
 * Progress segment from last reached upstream milestone (or campaign start) to this milestone.
 */
export function computeMilestoneProgressPercent(
  milestone,
  tasks,
  statuses,
  nestedSprints,
  allSprints,
  allMilestones,
) {
  if (isMilestoneReached(milestone, tasks, nestedSprints, statuses)) {
    return 100;
  }

  const missionTasks = getNonMilestoneTasks(tasks);
  const pathToMilestone = getCriticalPathMissionIdsForMilestone(
    milestone, tasks, statuses, nestedSprints, allSprints,
  );

  if (pathToMilestone.size === 0) return 0;

  const milestones = allMilestones?.length ? allMilestones : (tasks || []).filter(isMilestoneNode);
  const { segmentIds } = getMilestoneProgressSegmentIds(
    milestone, tasks, statuses, nestedSprints, allSprints, milestones,
  );

  if (segmentIds.size === 0) return 0;

  const taskMap = new Map(missionTasks.map((t) => [t.id, t]));
  let complete = 0;
  segmentIds.forEach((id) => {
    const t = taskMap.get(id);
    if (t && isTaskCompleteForProgress(t, statuses)) complete += 1;
  });

  return Math.round((complete / segmentIds.size) * 100);
}

/**
 * @returns {Array<{ milestone: object, progressPercent: number, reached: boolean, flowRank: number }>}
 */
export function computeMilestoneHudEntries(
  tasks,
  statuses = [],
  nestedSprints = [],
  allSprints = [],
) {
  const milestones = (tasks || []).filter(isMilestoneNode);
  if (milestones.length === 0) return [];

  const missionTasks = getNonMilestoneTasks(tasks);

  const entries = milestones.map((milestone) => {
    const reached = isMilestoneReached(milestone, tasks, nestedSprints, statuses);
    const rawProgress = reached
      ? 100
      : computeMilestoneProgressPercent(
        milestone, tasks, statuses, nestedSprints, allSprints, milestones,
      );
    return {
      milestone,
      progressPercent: rawProgress,
      reached,
      flowRank: getMilestoneFlowRank(milestone, missionTasks),
    };
  });

  const cappedEntries = applyProgressCaps(entries, missionTasks);

  return sortMilestoneHudEntries(cappedEntries, missionTasks);
}
